import { useContext, useReducer, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiContext, AuthContext, UserContext } from '../App';
import { resetPostHog } from '../lib/utils/posthog';
import useToast from './useToast';

const errorReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ERROR':
      return { error: action.payload };
    case 'CLEAR_ERROR':
      return { error: null };
    default:
      return state;
  }
};

function useErrorHandling() {
  const api = useContext(ApiContext);
  const navigate = useNavigate();
  const { setAuth } = useContext(AuthContext);
  const { setUser } = useContext(UserContext);
  const { makeToast } = useToast();
  const [error, setError] = useState();

  const [errorState, dispatch] = useReducer(errorReducer, { error: null });

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (requestError) => {
        if (requestError.response?.status === 401) {
          dispatch({ type: 'SET_ERROR', payload: { code: 401 } });
        }
        return Promise.reject(requestError);
      },
    );

    return () => api.interceptors.response.eject(interceptor);
  }, [api]);

  useEffect(() => {
    if (errorState.error) {
      if (errorState.error.code === 401) {
        makeToast(
          'You are being logged out due to session refresh',
          'negative-red',
          ['far', 'octagon-xmark'],
        );
        resetPostHog();
        window.localStorage.removeItem('auth');
        setAuth(null);
        setUser(null);
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
      if (errorState.error.code === 500) {
        makeToast(
          'Whoops, something went wrong! Please contact Credit Key Support for assistance: (844) 334-3636',
          'negative-red',
          ['far', 'octagon-xmark'],
        );
        navigate('/support');
      }
    } else {
      dispatch({ type: 'CLEAR_ERROR' });
      setError(errorState.error);
    }
  }, [errorState.error]);

  return { dispatch, error };
}

export default useErrorHandling;
